import { useState, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import InputMask from 'react-input-mask'
import Icon from '../Icon'
import DateTimePicker from './DateTimePicker'

// Initialize dayjs plugins
dayjs.extend(customParseFormat)

interface TextInputProps {
  entityID?: string
  label: string
  name: string
  type: string
  required?: boolean
  fixed?: boolean
  value: string
  group?: string
  groupError?: string
  regex?: RegExp
  columnSpec?: string
  onChange: any
  formatting?: string
}

const TextInput = ({
  entityID = '',
  type = 'text',
  required = false,
  fixed = false,
  label,
  name,
  value,
  group,
  groupError,
  regex,
  columnSpec,
  onChange,
  formatting,
}: TextInputProps) => {
  // Derive initial text value synchronously so label has correct position on first paint
  const deriveText = useCallback((): string => {
    if (!value) return ''

    if (type === 'date') {
      return value.includes('-') ? dayjs(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : value
    }

    return value
  }, [value, type])

  const [text, setText] = useState<string>(deriveText())
  const [isValid, setIsValid] = useState(true)
  const [isGroupValid, setIsGroupValid] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [validationAttempted, setValidationAttempted] = useState(false)
  const dateInputRef = useRef<HTMLInputElement>(null)

  let labelEx = label

  if (fixed) {
    labelEx += ` (fixed)`
  } else if (!required) {
    labelEx += ` (optional)`
  }

  useEffect(() => {
    // Don't override text while user is actively editing
    if (isEditing) return

    const newText = deriveText()
    setText(prev => (prev === newText ? prev : newText))

    if (type === 'date') {
      setIsValid(dayjs(newText, 'MM/DD/YYYY', true).isValid() || !required)
    } else {
      // For text/textArea types, validate required fields
      setIsValid(!required || Boolean(newText && newText.trim().length > 0))
    }
  }, [entityID, required, type, deriveText, isEditing, value])

  useEffect(() => {
    if (group != null && groupError === group && text.length === 0) {
      setIsGroupValid(false)
    } else {
      setIsGroupValid(true)
    }
  }, [group, text, groupError])

  // Listen for validation events from PropertyBar
  useEffect(() => {
    const handleValidate = () => {
      setValidationAttempted(true)
      // Re-validate when validation is triggered
      if (type === 'text' || type === 'textArea') {
        const val = text.trim()
        setIsValid(!required || val.length > 0)
      }
    }

    const handleClearValidation = () => {
      setValidationAttempted(false)
    }

    window.addEventListener('validateInputs', handleValidate)
    window.addEventListener('clearValidation', handleClearValidation)

    return () => {
      window.removeEventListener('validateInputs', handleValidate)
      window.removeEventListener('clearValidation', handleClearValidation)
    }
  }, [entityID, type, required, text])

  const handleChange = (e: any) => {
    let value = e.target.value

    // Mark as editing for date fields to prevent useEffect from overriding
    if (type === 'date') {
      setIsEditing(true)
    }

    switch (formatting) {
      case 'uppercase':
        value = value.toUpperCase()
        break
    }

    setText(value)

    if (!regex) {
      setIsValid(!required || (value && value.trim().length > 0))
    }

    if (typeof onChange === 'function') {
      // Don't call onChange for date during typing - wait for blur
      if (type !== 'date') {
        onChange(value)
      }
    }
  }

  const handleBlur = (e: any) => {
    if (regex) {
      const val = e.target.value.trim()
      setText(val)

      if (val.length === 0 && !required) {
        setIsValid(true)
      } else {
        setIsValid(new RegExp(regex).test(val))
      }
    } else if (type === 'text' || type === 'textArea') {
      // Validate required text/textArea fields on blur (only if validation was attempted)
      if (validationAttempted) {
        const val = e.target.value.trim()
        setIsValid(!required || val.length > 0)
      }
    }

    // For date fields, validate and convert on blur
    if (type === 'date' && e.target.value) {
      setIsEditing(false)
      const parsed = dayjs(e.target.value, 'MM/DD/YYYY', true)
      if (parsed.isValid() && typeof onChange === 'function') {
        onChange(parsed.format('YYYY-MM-DD'))
      }
    } else if (type === 'date') {
      setIsEditing(false)
    }
  }

  // Only show validation errors if validation has been attempted
  const shouldShowError = validationAttempted && !isValid
  const className = `input-container ${shouldShowError ? 'not-valid' : 'valid'}${isGroupValid ? '' : ' group-not-valid'} type-${type} ${columnSpec == null ? '' : 'column-' + columnSpec}`
  const groupName = group != null ? `group-${group}` : ''

  switch (type) {
    case 'textArea':
      return (
        <div className={className}>
          <textarea
            className={groupName}
            required={required!}
            readOnly={fixed}
            name={name}
            rows={15}
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <label className={text && 'filled'} htmlFor={name}>
            {labelEx}
          </label>
        </div>
      )

    case 'dayOfWeek':
      return (
        <div className={className}>
          <select className={groupName} required={required!} name={name} value={text} onChange={handleChange} onBlur={handleBlur}>
            <option value="" className="empty-option"></option>
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, idx) => (
              <option key={idx} value={idx}>
                {d}
              </option>
            ))}
          </select>
          <label className={text && 'filled'} htmlFor={name}>
            {labelEx}
          </label>
        </div>
      )

    case 'date': {
      const handleCalendarClick = () => {
        dateInputRef.current?.showPicker?.()
      }

      return (
        <div className={className} style={{ position: 'relative' }}>
          <InputMask
            mask="99/99/9999"
            placeholder={text.length > 0 ? 'MM/DD/YYYY' : ''}
            maskChar=""
            className={groupName}
            required={required!}
            readOnly={fixed}
            name={name}
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <input
            ref={dateInputRef}
            type="date"
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: 0,
              height: 0,
            }}
            onChange={e => {
              if (e.target.value) {
                const parsed = dayjs(e.target.value)
                if (parsed.isValid()) {
                  onChange(parsed.format('YYYY-MM-DD'))
                }
              }
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: fixed ? 'default' : 'pointer',
              pointerEvents: fixed ? 'none' : 'auto',
              opacity: fixed ? 0.5 : 1,
            }}
            onClick={handleCalendarClick}
          >
            <Icon name="Calendar" />
          </div>
          <label className={text && 'filled'} htmlFor={name}>
            {labelEx}
          </label>
        </div>
      )
    }

    case 'datetime':
      return (
        <div className={`${className} type-datetime`}>
          <DateTimePicker
            dateLabel={labelEx}
            value={value || null}
            onChange={val => {
              if (typeof onChange === 'function') {
                onChange(val ?? '')
              }
            }}
            disabled={fixed}
            required={required}
          />
        </div>
      )

    default:
      return (
        <div className={className}>
          <input className={groupName} required={required!} readOnly={fixed} name={name} type={type} value={text} onChange={handleChange} onBlur={handleBlur} />
          <label className={text && 'filled'} htmlFor={name}>
            {labelEx}
          </label>
        </div>
      )
  }
}

export default TextInput
